import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import DeprecateIcon from '../core/icons/Deprecate';
import UndeprecateIcon from '../core/icons/Undeprecate';
import PropTypes from 'prop-types';
import React from 'react';
import ActionText from '../core/ActionText';
import AreYouSure from '../core/AreYouSure';
import BasicReadField from '../core/BasicReadField';
import Identifier from '../core/Identifier';
import { globalStyles as styles } from '../core/style/gstheme';
import { checkResourceEntitlements } from '../core/utils/utils';
import ViewOnly from '../core/ViewOnly';
import withGS from '../core/withGShoc';
import { STATUS_ACCEPTED, STATUS_REJECTED } from '../core/utils/constants';

const APPROVED = STATUS_ACCEPTED;
const REJECTED = STATUS_REJECTED;

class ResourceSummaryDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            areYouSure: false
        };
    }

    checkIfSure = () => this.setState({ areYouSure: true });

    onSure = () => {
        this.setState({ areYouSure: false });
        this.props.onDeleteResource(this.props.data.id);
    };

    onNotSure = () => this.setState({ areYouSure: false });

    deprecateResource = () => {
        this.props.data.hasOwnProperty("deprecated") && !this.props.data.deprecated === undefined ?
            this.props.onUpdate('deprecated', true)
            :
            this.props.onAdd('deprecated', true);
    };

    undeprecateResource = () => {
        if (this.props.data.hasOwnProperty("deprecated")) this.props.onDelete('deprecated', true);
    };

    render() {

        const { data, classes, id, defaultLang, allowIRIchange, permission, name, canDeprecate, additionalViews,
            additionalActions, isReadOnly, viewLang, graph, baseIRI, project, useWorkflow } = this.props;
        const { areYouSure } = this.state;


        if (data._notAvailable) return <div>
            <BasicReadField margin="dense" text={id} fullWidth />
            <ActionText offset="30px">This resource is not available. It may have been deleted.</ActionText>
        </div>;

        if (data.pendingReplacement) return <ActionText>This resource has been replaced. Select again to view new data.</ActionText>;

        const isApproved = useWorkflow && "status" in data && data.status[0] === APPROVED ? true : false;
        const isRejected = useWorkflow && "status" in data && data.status[0] === REJECTED ? true : false;

        const readOnly = isReadOnly || checkResourceEntitlements(data, [permission]) || isApproved;

        const displayLang = viewLang && data.hasOwnProperty(name) && data[name][viewLang] ?
            viewLang
            :
            data.hasOwnProperty(name) && data[name][defaultLang] ?
                defaultLang
                :
                data.hasOwnProperty(name) && data[name]["und"] ?
                    "und"
                    :
                    defaultLang;

        const label = data.hasOwnProperty(name) && data[name][displayLang] ? (
            Array.isArray(data[name][displayLang]) ?
                data[name][displayLang].sort().join(" AND ") : data[name][displayLang]
        ) : 'No label available';

        const currentIRI = data.iri && data.iri.length > 0 ?
            data.iri[data.iri.length - 1]
            :
            data.id;
        const fallbackLang = (viewLang && viewLang !== defaultLang && displayLang === defaultLang)
            || (displayLang === defaultLang && (!data.hasOwnProperty(name) || !data[name][defaultLang]))
            || (displayLang === "und" && viewLang !== "und" && defaultLang !== "und");

        return (
            <div>
                <form autoComplete="off">
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={9}>
                            <Typography
                                data-fallbacklang={fallbackLang}
                                data-test="resource-heading" variant="h6" className={classes.mainHeading}
                                style={fallbackLang ? { color: '#FF6F00', wordBreak: 'break-word' } : { wordBreak: 'break-word' }}>
                                {label}
                                {(readOnly || isRejected) && <ViewOnly />}</Typography>
                        </Grid>
                        <Grid item xs={12} md={3} style={{ textAlign: 'right' }}>
                            {additionalViews}
                            {data.deprecated === true || isRejected ? null : additionalActions}
                            {!readOnly &&
                                <React.Fragment>
                                    {canDeprecate && (!data.hasOwnProperty("deprecated") || data.deprecated !== true) && !isRejected &&
                                        <span style={{ marginLeft: 6 }}>
                                            <Tooltip placement="right" title='Deprecate'>
                                                <IconButton data-test="deprecate-resource" size="small" onClick={this.deprecateResource}>
                                                    <DeprecateIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </span>
                                    }
                                    {canDeprecate && data.deprecated === true && !isRejected &&
                                        <span style={{ marginLeft: 6 }}>
                                            <Tooltip placement="right" title='Undeprecate'>
                                                <IconButton data-test="undeprecate-resource" size="small" onClick={this.undeprecateResource}>
                                                    <UndeprecateIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </span>
                                    }
                                    <span style={{ marginLeft: 6 }}>
                                        <Tooltip placement="right" title='Delete'>
                                            <IconButton data-test="delete-resource" size="small" onClick={this.checkIfSure}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </span>
                                </React.Fragment>
                            }
                        </Grid>
                        <Grid item xs={12}>
                            <Identifier
                                dataTest="identifier"
                                type={data.type}
                                project={project}
                                isReadOnly={readOnly || !allowIRIchange || data.deprecated || isRejected}
                                currentIRI={currentIRI}
                                identifier={id}
                                graph={graph}
                                baseIRI={baseIRI} />
                        </Grid>
                    </Grid>
                </form>
                {areYouSure && <AreYouSure onSure={this.onSure} onNotSure={this.onNotSure} message={[
                    "Do you really want to delete?",
                    "Note: Deleted item IRIs will not be available for reuse for a few minutes."
                ]}
                />}
            </div>
        );
    }
}

ResourceSummaryDetail.defaultProps = {
    name: 'label',
    isReadOnly: false,
    allowIRIchange: true,
    useWorkflow: false,
    canDeprecate: false
};

ResourceSummaryDetail.propTypes = {
    onAdd: PropTypes.func,
    onDelete: PropTypes.func,
    onUpdate: PropTypes.func,
    classes: PropTypes.object,
    project: PropTypes.string,
    graph: PropTypes.string.isRequired,
    allowIRIchange: PropTypes.bool,
    id: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    baseIRI: PropTypes.string,
    useWorkflow: PropTypes.bool,
    name: PropTypes.string,
    isReadOnly: PropTypes.bool.isRequired,
    additionalViews: PropTypes.object,
    additionalActions: PropTypes.object,
    viewLang: PropTypes.string,
    defaultLang: PropTypes.string.isRequired,
    permission: PropTypes.string.isRequired,
    onDeleteResource: PropTypes.func.isRequired,
    canDeprecate: PropTypes.bool.isRequired
};

export default withStyles(styles)(withGS(ResourceSummaryDetail, ["ALL"], null, {}, true));